import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ProductVariant } from '../../interfaces/wheels';
import { WheelCartService, CartItem } from '../../services/wheel-cart.service';
import { WheelPricingService } from '../../services/wheel-pricing.service';

@Component({
  selector: 'app-wheel-regular-size-selector',
  templateUrl: './wheel-regular-size-selector.component.html',
  styleUrls: ['./wheel-regular-size-selector.component.scss']
})
export class WheelRegularSizeSelectorComponent implements OnInit, OnChanges {
  @Input() product: ProductVariant;
  @Input() vehicleWheels: ProductVariant[];
  @Input() checkfitment: boolean = false;
  @Output() onCartItemsChange = new EventEmitter<CartItem[]>();
  @Output() onPriceChange = new EventEmitter<number>();

  // Size selection
  selectedWheelSize: string | number = '';
  
  // Quantity
  quantity = 4; // Default to set of 4 wheels
  
  // Pricing
  unitPrice = 0;
  totalPrice = 0;
  salePrice = 0;
  showSalePrice = false;

  constructor(
    private wheelCartService: WheelCartService,
    private wheelPricingService: WheelPricingService
  ) { }

  ngOnInit(): void {
    this.initializePricing();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product || changes.vehicleWheels) {
      this.initializePricing();
    }
  }

  /**
   * Gets the available wheel options for selection
   */
  get availableWheels(): ProductVariant[] {
    return this.vehicleWheels || [];
  }

  /**
   * Handles wheel size selection
   */
  onSizeSelect(event: any): void {
    const wheelId = event.target.value;
    const wheel = this.availableWheels.find(w => w.id == wheelId);
    
    if (!wheel) {
      this.selectedWheelSize = '';
      this.resetPricing();
      this.updateCartItems();
      return;
    }

    this.selectedWheelSize = wheelId;
    this.updatePricing();
    this.updateCartItems();
  }

  /**
   * Adjusts quantity for wheels
   */
  adjustQuantity(delta: number): void {
    const newQuantity = this.quantity + delta;
    
    // Validate quantity range (1-8 wheels, typically 4)
    if (newQuantity >= 1 && newQuantity <= 8) {
      this.quantity = newQuantity;
      this.updatePricing();
      this.updateCartItems();
    }
  }

  /**
   * Formats wheel display text for dropdown options
   */
  formatWheelOption(wheel: ProductVariant): string {
    const size = wheel.size || `${wheel.rim_diameter}x${wheel.rim_width}`;
    const offset = wheel.offset || 'N/A';
    const boltPattern = wheel.bolt_pattern || 'N/A';
    
    return `Size ${size}, Offset ${offset}, Bolt Pattern ${boltPattern}`;
  }

  /**
   * Initializes pricing based on product data
   */
  private initializePricing(): void {
    if (!this.product) return;

    this.unitPrice = this.product.uae_retail_price || this.product.price || 0;
    this.updatePricing();
  }

  /**
   * Updates pricing calculations
   */
  private updatePricing(): void {
    if (!this.product) {
      this.resetPricing();
      return;
    }

    // Calculate regular pricing
    this.totalPrice = this.wheelPricingService.calculateRegularPrice(this.product, this.quantity);

    // Handle sale pricing
    if (this.product.sale_price && this.product.sale_price > 0) {
      this.showSalePrice = true;
      this.salePrice = this.product.sale_price * this.quantity;
      this.totalPrice = this.salePrice;
    } else {
      this.showSalePrice = false;
      this.salePrice = 0;
    }

    this.onPriceChange.emit(this.totalPrice);
  }

  /**
   * Resets pricing to zero
   */
  private resetPricing(): void {
    this.totalPrice = 0;
    this.salePrice = 0;
    this.showSalePrice = false;
    this.onPriceChange.emit(0);
  }

  /**
   * Updates cart items and emits changes
   */
  private updateCartItems(): void {
    if (!this.product || !this.selectedWheelSize) {
      this.onCartItemsChange.emit([]);
      return;
    }

    const items = this.wheelCartService.buildRegularCartItems(
      this.product,
      this.selectedWheelSize,
      this.quantity
    );
    
    this.onCartItemsChange.emit(items);
  }

  /**
   * Gets the effective price for display (considering sale prices)
   */
  getEffectivePrice(): number {
    return this.showSalePrice ? this.salePrice : this.totalPrice;
  }

  /**
   * Gets the unit price for display
   */
  getUnitPrice(): number {
    if (this.showSalePrice && this.product?.sale_price) {
      return this.product.sale_price;
    }
    return this.unitPrice;
  }

  /**
   * Checks if pricing is available
   */
  hasPricing(): boolean {
    return !!(this.product?.uae_retail_price || this.product?.price);
  }

  /**
   * Gets quantity options for display (common wheel set sizes)
   */
  getQuantityOptions(): number[] {
    return [1, 2, 4, 6, 8]; // Common wheel quantities
  }

  /**
   * Checks if a quantity is the standard set size
   */
  isStandardSet(qty: number): boolean {
    return qty === 4;
  }
}
